import React from "react";
import "./icon-action-menu.scss";


/** Variant for menu item styling. 'default' = black, 'danger' = red (e.g. delete). */
export type IconActionMenuItemVariant = "default" | "danger";

export interface IconActionMenuItem {
  /** Unique id for the item (for keys and callbacks). */
  id: string;
  /** Display label. */
  label: string;
  /** Icon element (e.g. from react-feather or custom SVG). Optional. */
  icon?: React.ReactNode;
  /** Click handler. */
  onClick: (item: IconActionMenuItem) => void;
  /** Visual variant: default (black) or danger (red). */
  variant?: IconActionMenuItemVariant;
  /** Override icon color (overrides variant color when set). */
  iconColor?: string;
  /** Override label color (overrides variant color when set). */
  labelColor?: string;
  /** Disabled state – item not clickable and visually muted. */
  disabled?: boolean;
  /** Optional aria-label for accessibility (defaults to label). */
  ariaLabel?: string;
}

export interface IconActionMenuProps {
  /** List of menu items. */
  items: IconActionMenuItem[];
  /** Additional CSS class for the root container. */
  className?: string;
  /** Padding for the menu container (CSS value). Default matches Figma: 10px 16px. */
  padding?: string;
  /** Gap between items (CSS value). Default: 10px. */
  gap?: string;
  /** Size of the icon wrapper in pixels. Default: 18. */
  iconSize?: number;
  /** Callback when any item is clicked (after item.onClick). */
  onItemClick?: (item: IconActionMenuItem) => void;
  /** Role for the list container. Default: "menu". */
  role?: "menu" | "list";
  /** Optional id for the menu (e.g. for aria-labelledby). */
  id?: string;
}

const DEFAULT_PADDING = "10px 16px";
const DEFAULT_GAP = "10px";
const DEFAULT_ICON_SIZE = 18;

export function IconActionMenu({
  items,
  className = "",
  padding = DEFAULT_PADDING,
  gap = DEFAULT_GAP,
  iconSize = DEFAULT_ICON_SIZE,
  onItemClick,
  role = "menu",
  id,
}: IconActionMenuProps) {
  return (
    <div
      id={id}
      role={role}
      className={`icon-action-menu ${className}`.trim()}
      style={{
        padding,
        gap,
        ["--icon-action-menu-icon-size" as string]: `${iconSize}px`,
      }}
    >
      {items.map((item) => {
        const iconColor = item.iconColor;
        const labelColor = item.labelColor;

        return (
          <div
            key={item.id}
            role="menuitem"
            aria-label={item.ariaLabel ?? item.label}
            aria-disabled={item.disabled}
            className={`icon-action-menu__item icon-action-menu__item--${item.variant ?? "default"} ${item.disabled ? "icon-action-menu__item--disabled" : ""}`}
            onClick={() => {
              if (item.disabled) return;
              item.onClick(item);
              onItemClick?.(item);
            }}
            onKeyDown={(e) => {
              if (item.disabled) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                item.onClick(item);
                onItemClick?.(item);
              }
            }}
            tabIndex={item.disabled ? -1 : 0}
            style={{
              ...(labelColor && { color: labelColor }),
            }}
          >
            {item.icon != null && (
              <span
                className="icon-action-menu__icon"
                style={{
                  ...(iconColor && { color: iconColor }),
                }}
                aria-hidden
              >
                {item.icon}
              </span>
            )}
            <span className="icon-action-menu__label">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}
